import { useEffect, useRef, useState } from 'react';
import { TranslationService } from '../../../services/translation.service';
import OverlayBox from '../../Shared/OverlayBox';
import { SanitizerService } from '../../../services/sanitizer.service';
import { AnalyticsService } from '../../../services/analytics-service';
import { LocationType } from '../../../models/location-type.enum';
import LocationAutocomplete, { type ILocationAutocompleteSelection, type ILocationAutocompleteState } from '../../Shared/LocationAutocomplete';
import type { IStopArea } from '../../../models/stop-area.model';
import type { ITariffZoneDetails } from '../../../models/tariff-zone-details.model';
import MapFilters, { type IMapFiltersSelection } from './components/MapFilters';
import { StopAreaApiModel } from '../models/stop-area.api-model';
import { TariffZoneDetailsApiModel } from '../models/tariff-zone-details.api-model';
import TariffZoneSelector from '../../Shared/TariffZoneSelector';
import StopAreaMap from '../StopAreaMap';
import './style.scss';
import type { IAutocomplete } from '../../Shared/AutocompleteBase';
import type { IGenericLocation } from '../../Shared/Map/markers/GenericLocationMarker';
import StopAreaMapHelper from '../StopAreaMap/helpers/stop-area-map.helper';
import ValidationInfo from '../../../validation/validation-info';
import useFetch, { Status } from '../../../hooks/fetch.hook';
import { CommuterParkingLotApiModel } from '../models/commuter-parking-lot.api-model';
import { ICommuterParkingLot } from '../../../models/commuter-parking-lot.model';
import { TicketOfficeApiModel } from '../models/ticket-office.api.model';
import { ITicketOffice } from '../../../models/ticket-office.model';

function processReceivedStopAreaData(data: StopAreaApiModel[] | undefined): IStopArea[] | undefined {
  if (!data) return [];
  return data?.map(StopAreaApiModel.mapToBusinessModel) ?? undefined;
}

function processReceivedTrafiffZoneData(data: TariffZoneDetailsApiModel[] | undefined): ITariffZoneDetails[] {
  return data?.map(TariffZoneDetailsApiModel.mapToBusiness) ?? [];
}

function processReceivedCommuterParkingLotsData(data: CommuterParkingLotApiModel[] | undefined, mapFiltersSelection: IMapFiltersSelection): ICommuterParkingLot[] | undefined {
  if (!mapFiltersSelection.showCommuterParkingLots || !data) return [];
  return data?.map(CommuterParkingLotApiModel.mapToBusinessModel) ?? [];
}

function processReceivedTicketOfficesData(data: TicketOfficeApiModel[] | undefined, mapFiltersSelection: IMapFiltersSelection): ITicketOffice[] | undefined {
  if (!mapFiltersSelection.showTicketOffices || !data) return [];
  return data?.map(TicketOfficeApiModel.mapToBusinessModel) ?? [];
}

function StopAreaSearch() {
  // === STATE ===
  const [locationAutocompleteState, setLocationAutocompleteState] = useState<ILocationAutocompleteState>({
    value: undefined,
    validationInfo: new ValidationInfo(),
  });
  const [selectedTariffZones, setSelectedTariffZones] = useState<ITariffZoneDetails[]>([]);
  const [{ status: getStopAreasDataStatus, data: getStopAreasData }] = useFetch<StopAreaApiModel[]>('/api/stop-area-search/stopareas');
  const [{ data: getTariffZonesData }] = useFetch<TariffZoneDetailsApiModel[]>('/api/stop-area-search/tariffzones');
  const [{ data: getCommuterParkingLotsData }] = useFetch<CommuterParkingLotApiModel[]>('/api/stop-area-search/commuterparkinglots');
  const [{ data: getTicketOfficesData }] = useFetch<TicketOfficeApiModel[]>('/api/stop-area-search/ticketoffices');
  const [genericMarker, setGenericMarker] = useState<IGenericLocation | undefined>(undefined);

  const [mapFiltersSelection, setMapFiltersSelection] = useState<IMapFiltersSelection>({
    showStopAreas: true,
    showCommuterParkingLots: false,
    showTicketOffices: false,
  });

  // === REFS ===
  const locationAutocompleteRef = useRef<IAutocomplete>(null);

  // === VARIABLES ===
  const stopAreas = processReceivedStopAreaData(getStopAreasData);
  const tariffZones = processReceivedTrafiffZoneData(getTariffZonesData);
  const commuterParkingLots = processReceivedCommuterParkingLotsData(getCommuterParkingLotsData, mapFiltersSelection);
  const ticketOffices = processReceivedTicketOfficesData(getTicketOfficesData, mapFiltersSelection);

  const [isFilterApplied, setIsFilterApplied] = useState(false);

  function getStopAreaGidFromQueryString(): string | undefined {
    return new URLSearchParams(window.location.search).get('hp') ?? undefined;
  }

  if (getStopAreasDataStatus === Status.failed) {
    AnalyticsService.addEvent(AnalyticsService.StopArea, AnalyticsService.ErrorAction, `Hämta hållplatser - ${AnalyticsService.InternalErrorLabel}`);
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedTariffZones(processReceivedTrafiffZoneData(getTariffZonesData));
  }, [getTariffZonesData]);

  function handleOnLocationSelected(item: ILocationAutocompleteSelection | undefined) {
    if (item && item.location) {
      if (item.location.gid) {
        // This call isn't working, the cluster marker has not been associated with the map
        // in the underlying component. The highlight is working since it is triggered by
        // pushing the gid to the query string on line 99 below in this metod.
        // stopAreaMapRef.current.highlightStopArea(item.location.gid);
        if (genericMarker) setGenericMarker(undefined);
      } else {
        setGenericMarker({
          text: item.location.name ?? '',
          altText: `${TranslationService.get('stopareasearchpage.selectedlocation')}: ${item.location.name}`,
          latitude: item.location.latitude ?? 0,
          longitude: item.location.longitude ?? 0,
          autoOpenPopup: true,
        });
      }
      setLocationAutocompleteState({
        value: undefined,
        validationInfo: new ValidationInfo(),
      });
      // This call triggers zooming in on stop area and showing the popup
      window.history.pushState({}, '', item.location.locationType === LocationType.stopArea ? `?hp=${item.location.gid}` : '?');
      AnalyticsService.addEvent(AnalyticsService.StopArea, 'Sök hållplats', item.location.name);
      locationAutocompleteRef.current?.clear();
      setLocationAutocompleteState({
        value: undefined,
        validationInfo: new ValidationInfo(),
      });
    }
  }

  function handleOnTariffZoneSelectorChange(newSelectedTariffZones: ITariffZoneDetails[]) {
    AnalyticsService.addEvent(
      AnalyticsService.StopArea,
      'Zon',
      newSelectedTariffZones
        .map((z) => z.shortName)
        .sort()
        .join(','),
    );

    setSelectedTariffZones(newSelectedTariffZones);
  }

  function handleOnMapFiltersSelectionChange(newSelection: IMapFiltersSelection) {
    setMapFiltersSelection(newSelection);
    setIsFilterApplied(true);
  }

  function handleOnOverlayBoxLoad() {
    StopAreaMapHelper.moveBreadcrumbs('stopareasearchpage-breadcrumbs');
  }

  return (
    <div className="stop-area-search" role="search" aria-label={TranslationService.get('stopareasearchpage.searchlabel')}>
      <OverlayBox
        headerText={TranslationService.get('stopareasearchpage.heading')}
        contentText={TranslationService.get('stopareasearchpage.textcontent')}
        contentTextCssClass=""
        absolutePositionedMd={false}
        onLoad={handleOnOverlayBoxLoad}
      >
        <label id="location-autocomplete-label" htmlFor="location-autocomplete" className="form-label">
          {TranslationService.get('stopareasearchpage.searchboxlabel')}
        </label>
        <LocationAutocomplete
          id="location-autocomplete"
          /* @ts-expect-error ts(2322) */
          ref={locationAutocompleteRef}
          className="mb-3"
          locationTypes={[LocationType.stopArea, LocationType.address, LocationType.pointOfInterest]}
          placeholderText={TranslationService.get('stopareasearchpage.searchboxplaceholdertext')}
          enableCurrentPosition={false}
          listAriaLabelledBy="location-autocomplete-label"
          state={locationAutocompleteState}
          onSelect={handleOnLocationSelected}
        />
        {TranslationService.get('stopareasearchpage.additional3zonertextcontent') && (
          <div
            dangerouslySetInnerHTML={{
              __html: SanitizerService.sanitizeHtml(TranslationService.get('stopareasearchpage.additional3zonertextcontent')),
            }}
          ></div>
        )}
        <p>{TranslationService.get('stopareasearchpage.tariffzoneexpandablebuttontext')}:</p>
        <TariffZoneSelector className="mt-3" availableTariffZones={tariffZones} selectedTariffZones={selectedTariffZones} onChange={handleOnTariffZoneSelectorChange} />
        <MapFilters className="mt-3" selection={mapFiltersSelection} onChange={handleOnMapFiltersSelectionChange} />
        {TranslationService.get('stopareasearchpage.more3zonertextcontent') && (
          <div
            dangerouslySetInnerHTML={{
              __html: SanitizerService.sanitizeHtml(TranslationService.get('stopareasearchpage.more3zonertextcontent')),
            }}
            className="more-information-text mt-2"
          ></div>
        )}
      </OverlayBox>
      <StopAreaMap
        mapClassName="stop-area-search__map"
        stopAreas={stopAreas}
        commuterParkingLots={commuterParkingLots}
        ticketOffices={ticketOffices}
        genericMarker={genericMarker}
        tariffZones={selectedTariffZones}
        highLightedStopAreaGid={getStopAreaGidFromQueryString()}
        isFilterApplied={isFilterApplied}
        mapFiltersSelection={mapFiltersSelection}
      />
    </div>
  );
}

export default StopAreaSearch;
